import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc,
  onSnapshot, query, where, orderBy, serverTimestamp, setDoc, Timestamp
} from "firebase/firestore";
import {
  signInWithEmailAndPassword, signOut, onAuthStateChanged
} from "firebase/auth";
import {
  getStorage, ref, uploadBytes, getDownloadURL, deleteObject
} from "firebase/storage";
import { db, auth, storage } from "./config";

// ─── AUTH ───────────────────────────────────────────────
export const login = (email, password) =>
  signInWithEmailAndPassword(auth, email, password);

export const logout = () => signOut(auth);

export const onAuth = (cb) => onAuthStateChanged(auth, cb);

// ─── CHAMBRES ───────────────────────────────────────────
export const getRooms = (cb) => {
  const q = query(collection(db, "rooms"));
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.number || "").localeCompare(b.number || ""))
    )
  );
};

export const getRoom = async (roomId) => {
  const snap = await getDoc(doc(db, "rooms", roomId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const updateRoom = (roomId, data) =>
  updateDoc(doc(db, "rooms", roomId), data);

// ─── MESSAGES ───────────────────────────────────────────
export const getMessages = (roomId, cb) => {
  const q = query(collection(db, "rooms", roomId, "messages"));
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0))
    )
  );
};

export const sendMessage = (roomId, { text, senderId, senderRole, type = "text", ticketId = null }) =>
  addDoc(collection(db, "rooms", roomId, "messages"), {
    text,
    senderId,
    senderRole,
    type,
    ticketId,
    createdAt: serverTimestamp(),
    read: false
  });

export const markMessagesRead = async (roomId, currentUserId) => {
  const q = query(
    collection(db, "rooms", roomId, "messages"),
    where("read", "==", false)
  );
  const snap = await getDocs(q);
  const updates = snap.docs
    .filter(d => d.data().senderId !== currentUserId)
    .map(d => updateDoc(d.ref, { read: true }));
  return Promise.all(updates);
};

// ─── TICKETS ────────────────────────────────────────────
export const getTickets = (cb, roomId = null) => {
  let q;
  if (roomId) {
    q = query(collection(db, "tickets"), where("roomId", "==", roomId));
  } else {
    q = query(collection(db, "tickets"));
  }
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))
    )
  );
};

export const createTicket = async (roomId, { category, description, photoUrl = null, userId = null, userName = null }) => {
  const ticketRef = await addDoc(collection(db, "tickets"), {
    roomId,
    category,
    description,
    photoUrl,
    userId,
    userName,
    status: "open",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    rating: null
  });
  if (roomId) {
    await sendMessage(roomId, {
      text: `Ticket signalé — ${category} : ${description}`,
      senderId: "system",
      senderRole: "system",
      type: "ticket_open",
      ticketId: ticketRef.id
    });
  }
  return ticketRef.id;
};

export const updateTicketStatus = async (ticketId, roomId, status) => {
  await updateDoc(doc(db, "tickets", ticketId), {
    status,
    updatedAt: serverTimestamp()
  });
  const labels = { prog: "En cours de traitement", done: "Résolu par IAM" };
  await sendMessage(roomId, {
    text: `Ticket mis à jour — ${labels[status] || status}`,
    senderId: "system",
    senderRole: "system",
    type: "ticket_update",
    ticketId
  });
};

export const rateTicket = (ticketId, rating) =>
  updateDoc(doc(db, "tickets", ticketId), { rating, updatedAt: serverTimestamp() });

// ─── PAIEMENTS ──────────────────────────────────────────
export const getPayments = (cb, roomId = null) => {
  let q;
  if (roomId) {
    q = query(collection(db, "payments"), where("roomId", "==", roomId));
  } else {
    q = query(collection(db, "payments"));
  }
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))
    )
  );
};

export const declarePayment = async (roomId, { amount, method, reference, month, monthLabel = null, validatedByOwner = false }) => {
  // GARDE-FOU : aucune transaction sans chambre valide
  if (!roomId) {
    throw new Error("Impossible d'enregistrer un paiement : aucune chambre associée.");
  }
  await addDoc(collection(db, "payments"), {
    roomId,
    amount,
    method,
    reference,
    month,
    monthLabel,
    validatedByOwner,
    status: "pending",
    createdAt: serverTimestamp()
  });
  await updateRoom(roomId, { paymentStatus: "pending" });
};

export const confirmPayment = async (paymentId, roomId) => {
  // GARDE-FOU : ne pas confirmer un paiement orphelin
  if (!roomId) {
    throw new Error("Impossible de confirmer : ce paiement n'est lié à aucune chambre.");
  }
  await updateDoc(doc(db, "payments", paymentId), {
    status: "confirmed",
    confirmedAt: serverTimestamp()
  });
  await updateRoom(roomId, { paymentStatus: "paid", lastPayment: serverTimestamp() });
  await sendMessage(roomId, {
    text: "Paiement de 27 500 FCFA confirmé par IAM Business Group.",
    senderId: "system",
    senderRole: "system",
    type: "payment_confirmed"
  });
};

export const deletePayment = async (paymentId) => {
  await deleteDoc(doc(db, "payments", paymentId));
};

// ─── CONTRAT DE LOCATION (archivage PDF) ─────────────────
// Upload le PDF signé dans Storage et enregistre le lien sur la chambre
export const saveRoomContract = async (roomId, pdfBlob, meta = {}) => {
  if (!roomId) throw new Error("Chambre requise pour archiver le contrat.");
  const path = `contracts/${roomId}/contrat_${Date.now()}.pdf`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, pdfBlob, { contentType: "application/pdf" });
  const url = await getDownloadURL(storageRef);
  await updateDoc(doc(db, "rooms", roomId), {
    contractUrl: url,
    contractPath: path,
    contractSignedAt: serverTimestamp(),
    contractTenantName: meta.tenantName || "",
    updatedAt: serverTimestamp(),
  });
  return url;
};

// ─── NOTIFICATIONS ──────────────────────────────────────
export const sendNotification = async (roomId, { title, body, type }) => {
  await addDoc(collection(db, "notifications"), {
    roomId,
    title,
    body,
    type,
    createdAt: serverTimestamp(),
    read: false
  });
};

export const sendBulkReminder = async (rooms) => {
  const batch = rooms.map(room =>
    sendNotification(room.id, {
      title: "Rappel de paiement — IAM Business Group",
      body: `Votre loyer de 27 500 FCFA est dû le ${room.dueDay || 10} du mois.`,
      type: "payment_reminder"
    })
  );
  return Promise.all(batch);
};

// ─── ANNONCES DE CHAMBRES DISPONIBLES ───────────────────
export const getListings = (cb) => {
  const q = query(collection(db, "listings"));
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))
    )
  );
};

export const getActiveListing = (cb) => {
  const q = query(collection(db, "listings"), where("status", "in", ["available", "soon"]));
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))
    )
  );
};

export const createListing = async ({
  title, description, price, roomNumber,
  availableFrom, status, photos = []
}) => {
  return addDoc(collection(db, "listings"), {
    title,
    description,
    price,
    roomNumber,
    availableFrom,
    status,        // "available" | "soon" | "closed"
    photos,
    interestCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateListing = (listingId, data) =>
  updateDoc(doc(db, "listings", listingId), { ...data, updatedAt: serverTimestamp() });

export const deleteListing = (listingId) =>
  deleteDoc(doc(db, "listings", listingId));

// ─── INTÉRÊTS (bouton "Je suis intéressé") ──────────────
export const getInterests = (listingId, cb) => {
  const q = query(
    collection(db, "listings", listingId, "interests"),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
};

export const getAllInterests = (cb) => {
  // Simple query without orderBy to avoid index requirements
  const q = query(collection(db, "interests"));
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() || 0;
        const tb = b.createdAt?.toMillis?.() || 0;
        return tb - ta;
      })
    )
  );
};

export const expressInterest = async (listingId, { userId, userName, roomId, message = "" }) => {
  // Check if already expressed interest
  const q = query(
    collection(db, "interests"),
    where("listingId", "==", listingId),
    where("userId", "==", userId)
  );
  const existing = await getDocs(q);
  if (!existing.empty) return { alreadyExists: true };

  const interestRef = await addDoc(collection(db, "interests"), {
    listingId,
    userId,
    userName,
    roomId,
    message,
    status: "pending",   // "pending" | "accepted" | "refused"
    createdAt: serverTimestamp()
  });
  // Increment counter on listing
  const listingRef = doc(db, "listings", listingId);
  const listingSnap = await getDoc(listingRef);
  if (listingSnap.exists()) {
    await updateDoc(listingRef, {
      interestCount: (listingSnap.data().interestCount || 0) + 1
    });
  }
  return { id: interestRef.id };
};

export const cancelInterest = async (listingId, userId) => {
  const q = query(
    collection(db, "interests"),
    where("listingId", "==", listingId),
    where("userId", "==", userId)
  );
  const snap = await getDocs(q);
  const deletions = snap.docs.map(d => deleteDoc(d.ref));
  await Promise.all(deletions);
  // Decrement counter
  const listingRef = doc(db, "listings", listingId);
  const listingSnap = await getDoc(listingRef);
  if (listingSnap.exists()) {
    const count = Math.max(0, (listingSnap.data().interestCount || 1) - 1);
    await updateDoc(listingRef, { interestCount: count });
  }
};

export const getUserInterest = async (listingId, userId) => {
  const q = query(
    collection(db, "interests"),
    where("listingId", "==", listingId),
    where("userId", "==", userId)
  );
  const snap = await getDocs(q);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
};

export const respondToInterest = async (interestId, status) => {
  // status: "accepted" | "refused"
  await updateDoc(doc(db, "interests", interestId), {
    status,
    respondedAt: serverTimestamp()
  });
};

// ─── UPLOAD FICHIERS (tickets) avec TTL 7 jours ─────────
export const uploadTicketFile = async (ticketId, file) => {
  const stor = getStorage();
  const ext = file.name.split(".").pop();
  const path = `tickets/${ticketId}/${Date.now()}.${ext}`;
  const storageRef = ref(stor, path);

  const metadata = {
    customMetadata: {
      // TTL hint for the Cloud Function cleanup (Unix timestamp + 7 days)
      expiresAt: String(Date.now() + 7 * 24 * 60 * 60 * 1000),
      ticketId
    }
  };

  await uploadBytes(storageRef, file, metadata);
  const url = await getDownloadURL(storageRef);

  // Register file in Firestore so the cleanup function can find it
  await addDoc(collection(db, "ticketFiles"), {
    ticketId,
    storagePath: path,
    url,
    fileName: file.name,
    size: file.size,
    uploadedAt: serverTimestamp(),
    expiresAt: Timestamp.fromMillis(Date.now() + 7 * 24 * 60 * 60 * 1000),
    deleted: false
  });

  return url;
};

// Called by Cloud Function or manual cleanup — deletes expired files
export const deleteExpiredFiles = async () => {
  const now = Timestamp.now();
  const q = query(
    collection(db, "ticketFiles"),
    where("expiresAt", "<=", now),
    where("deleted", "==", false)
  );
  const snap = await getDocs(q);
  const stor = getStorage();

  const tasks = snap.docs.map(async (d) => {
    const { storagePath, ticketId } = d.data();
    try {
      await deleteObject(ref(stor, storagePath));
    } catch (e) {
      // File may already be gone — continue
    }
    // Mark as deleted in Firestore and clear photoUrl on ticket
    await updateDoc(d.ref, { deleted: true });
    await updateDoc(doc(db, "tickets", ticketId), {
      photoUrl: null,
      photoExpired: true
    });
  });

  await Promise.all(tasks);
  return snap.size;
};

// ─── GESTION CHAMBRES (ajout/modif/suppression) ─────────
export const createRoom = async ({ number, tenantName, tenantEmail, loyer, dueDay }) => {
  const roomId = `room_${number}`;
  await setDoc(doc(db, "rooms", roomId), {
    number,
    tenantName: tenantName || "",
    tenantEmail: tenantEmail || "",
    loyer: loyer || 27500,
    dueDay: dueDay || 10,
    paymentStatus: tenantName ? "due_soon" : "vacant",
    createdAt: serverTimestamp()
  });
  return roomId;
};

export const updateRoomFull = (roomId, data) =>
  updateDoc(doc(db, "rooms", roomId), { ...data, updatedAt: serverTimestamp() });

export const deleteRoom = (roomId) =>
  deleteDoc(doc(db, "rooms", roomId));

// Crée ou met à jour le compte étudiant dans Firestore users/
// (l'email doit déjà exister dans Firebase Authentication)
export const linkStudentToRoom = async (roomId, { userId, name, email }) => {
  // GARDE-FOU : liaison impossible sans chambre ET utilisateur
  if (!roomId || !userId) {
    throw new Error("Chambre et étudiant obligatoires pour la liaison.");
  }
  await setDoc(doc(db, "users", userId), {
    role: "student",
    name,
    email,
    roomId
  });
  await updateDoc(doc(db, "rooms", roomId), {
    tenantName: name,
    tenantEmail: email,
    tenantId: userId,
    paymentStatus: "due_soon",
    updatedAt: serverTimestamp()
  });
};

export const unlinkStudentFromRoom = async (roomId, userId) => {
  if (userId) {
    await updateDoc(doc(db, "users", userId), { roomId: null });
  }
  await updateDoc(doc(db, "rooms", roomId), {
    tenantName: "",
    tenantEmail: "",
    tenantId: null,
    paymentStatus: "vacant",
    updatedAt: serverTimestamp()
  });
};

// ─── CRÉATION COMPTE ÉTUDIANT ────────────────────────────
// Utilise une Cloud Function pour créer le compte Firebase Auth
// sans déconnecter le propriétaire
export const createStudentAccount = async ({ email, password, name, roomId }) => {
  // Appel à la Cloud Function "createStudent"
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const functions = getFunctions(undefined, "europe-west1");
  const createStudent = httpsCallable(functions, "createStudent");
  const result = await createStudent({ email, password, name, roomId });
  return result.data; // { uid, email, name, roomId }
};

// Vérifie si un email est déjà utilisé dans Authentication
export const checkEmailExists = async (email) => {
  try {
    const { fetchSignInMethodsForEmail } = await import("firebase/auth");
    const methods = await fetchSignInMethodsForEmail(auth, email);
    return methods.length > 0;
  } catch {
    return false;
  }
};

// ─── DISPONIBILITÉ CHAMBRE ───────────────────────────────
export const setRoomAvailability = (roomId, { availableSoon, availableFrom, description }) =>
  updateDoc(doc(db, "rooms", roomId), {
    availableSoon: availableSoon || false,
    availableFrom: availableFrom || "",
    description:   description   || "",
    updatedAt: serverTimestamp()
  });

// ─── NOTIFICATIONS IN-APP (sans VAPID) ──────────────────
// Crée une notification Firestore visible dans l'appli
export const createNotification = async (roomId, { title, body, type, link = "" }) => {
  await addDoc(collection(db, "notifications"), {
    roomId,
    title,
    body,
    type,
    link,
    read: false,
    createdAt: serverTimestamp()
  });
};

export const getNotifications = (userId, role, roomId, cb) => {
  let q;
  if (role === "owner") {
    q = query(collection(db, "notifications"));
  } else {
    q = query(collection(db, "notifications"), where("roomId", "==", roomId));
  }
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
};

export const markNotificationRead = (notifId) =>
  updateDoc(doc(db, "notifications", notifId), { read: true });

export const markAllNotificationsRead = async (roomId, role) => {
  let q;
  if (role === "owner") {
    q = query(collection(db, "notifications"), where("read", "==", false));
  } else {
    q = query(collection(db, "notifications"), where("roomId", "==", roomId), where("read", "==", false));
  }
  const snap = await getDocs(q);
  await Promise.all(snap.docs.map(d => updateDoc(d.ref, { read: true })));
};


// ─── REJETER UN PAIEMENT ─────────────────────────────────
export const rejectPayment = async (paymentId, roomId, reason) => {
  if (!paymentId) return;
  await updateDoc(doc(db, "payments", paymentId), {
    status: "rejected", rejectedAt: serverTimestamp(), rejectReason: reason || ""
  });
  await updateRoom(roomId, { paymentStatus: "late" });
  const msg = reason
    ? `Paiement rejeté par IAM. Raison : ${reason}`
    : "Votre paiement a été rejeté. Veuillez contacter IAM Business Group.";
  await sendMessage(roomId, { text: msg, senderId: "system", senderRole: "system", type: "payment_rejected" });
  if (roomId) {
    await addDoc(collection(db, "notifications"), {
      roomId, title: "❌ Paiement rejeté",
      body: reason || "Votre paiement n'a pas pu être validé. Contactez IAM.",
      type: "payment_rejected", read: false, createdAt: serverTimestamp()
    });
  }
};

// ─── ANNONCES GÉNÉRIQUES ─────────────────────────────────
export const getAnnouncements = (cb) => {
  const q = query(collection(db, "announcements"));
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)))
  );
};

export const createAnnouncement = async ({ title, body, category, targetRoomId = null }) => {
  const ref = await addDoc(collection(db, "announcements"), {
    title, body, category, targetRoomId,
    createdAt: serverTimestamp(), updatedAt: serverTimestamp()
  });
  const notifBase = { title: `📢 ${title}`, body: body.slice(0, 80), type: "announcement", read: false, createdAt: serverTimestamp() };
  await addDoc(collection(db, "notifications"), { ...notifBase, roomId: targetRoomId || "all" });
  return ref.id;
};

export const updateAnnouncement = (id, data) =>
  updateDoc(doc(db, "announcements", id), { ...data, updatedAt: serverTimestamp() });

export const deleteAnnouncement = (id) => deleteDoc(doc(db, "announcements", id));

// ─── CAUTION DE GARANTIE ─────────────────────────────────
export const getCautions = (cb, roomId = null) => {
  const q = roomId
    ? query(collection(db, "cautions"), where("roomId", "==", roomId))
    : query(collection(db, "cautions"));
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)))
  );
};

export const setCaution = async (roomId, { status, amount, note, returnedAt = null }) => {
  if (!roomId) return;
  const q = query(collection(db, "cautions"), where("roomId", "==", roomId));
  const snap = await getDocs(q);
  const data = { roomId, status: status || "pending", amount: amount || 27500, note: note || "", returnedAt: returnedAt || null, updatedAt: serverTimestamp() };
  if (snap.empty) {
    await addDoc(collection(db, "cautions"), { ...data, createdAt: serverTimestamp() });
  } else {
    await updateDoc(snap.docs[0].ref, data);
  }
  if (status === "paid" && roomId) {
    await addDoc(collection(db, "notifications"), {
      roomId, title: "✅ Caution enregistrée",
      body: `Votre caution de ${(amount || 27500).toLocaleString("fr-FR")} FCFA a été enregistrée par IAM.`,
      type: "caution", read: false, createdAt: serverTimestamp()
    });
  }
  if (status === "returned" && roomId) {
    await addDoc(collection(db, "notifications"), {
      roomId, title: "💰 Caution remboursée",
      body: `Votre caution de ${(amount || 27500).toLocaleString("fr-FR")} FCFA a été remboursée.`,
      type: "caution", read: false, createdAt: serverTimestamp()
    });
  }
};

// ─── CAUTION DE RÉSERVATION ───────────────────────────────
export const declareCautionForRoom = async (myRoomId, targetRoomId, { amount, method, reference, interestId }) => {
  if (!targetRoomId) return;
  // GARDE-FOU : seul un locataire (avec chambre) peut réserver
  if (!myRoomId) {
    throw new Error("Vous devez être locataire pour réserver une chambre.");
  }
  await addDoc(collection(db, "cautions"), {
    roomId: targetRoomId, fromRoomId: myRoomId || null, interestId: interestId || null,
    status: "pending_confirmation", amount: amount || 27500,
    method: method || "", reference: reference || "", note: "Caution pour réservation",
    createdAt: serverTimestamp(), updatedAt: serverTimestamp()
  });
  await addDoc(collection(db, "notifications"), {
    roomId: "owner", title: "💰 Caution de réservation déclarée",
    body: `Un étudiant a déclaré une caution de ${(amount || 27500).toLocaleString("fr-FR")} FCFA via ${method || "—"}.`,
    type: "caution_declared", targetRoomId, read: false, createdAt: serverTimestamp()
  });
};

// Note : la réparation de liaison compte ↔ chambre se fait directement
// depuis OwnerRooms.jsx via linkStudentToRoom (déjà défini plus haut) —
// aucune Cloud Function n'est nécessaire, ce qui fonctionne même sans
// plan Firebase payant (Blaze).
